/**
 * Home page flavour (Sep 2026): a livelier greeting and a values quote.
 *
 * Greeting: picked by time of day and weekday, stable for the day (seeded by
 * the date, so it doesn't reshuffle on every render or tab switch).
 * Quote: one per DAAily value (the Drivers values: Drive, Reliability,
 * Authenticity, Empathy), rotating every three hours, the same for everyone.
 *
 * Quotes are limited to well-sourced attributions; add new ones only with a
 * source you have checked (misattributed quotes are everywhere).
 */

type Greeter = (name: string) => string;

const MORNING: Greeter[] = [
  (n) => `Rise and shine, ${n}.`,
  (n) => `Morning, ${n}! Coffee in hand?`,
  (n) => `Let's get rolling, ${n}.`,
  (n) => `Fresh start, ${n}. Let's go.`,
];
const AFTERNOON: Greeter[] = [
  (n) => `Keep the momentum, ${n}.`,
  (n) => `Afternoon, ${n}. Second wind?`,
  (n) => `Halfway there, ${n}. Nice work so far.`,
  (n) => `Good to see you, ${n}. What's next?`,
];
const EVENING: Greeter[] = [
  (n) => `Evening, ${n}. Let's wrap it up.`,
  (n) => `Still going strong, ${n}?`,
  (n) => `Last lap of the day, ${n}.`,
];
const LATE: Greeter[] = [
  (n) => `Burning the midnight oil, ${n}?`,
  (n) => `Night owl mode, ${n}. We'll keep it quick.`,
];
const MONDAY: Greeter[] = [(n) => `Fresh week, ${n}. Let's make it count.`, (n) => `New week, new wins, ${n}.`];
const FRIDAY: Greeter[] = [(n) => `Almost weekend, ${n}. Let's land it.`, (n) => `Friday, ${n}! Finish strong.`];
const WEEKEND: Greeter[] = [(n) => `Weekend mode, ${n}? We'll keep it short.`, (n) => `Look who's here on a weekend, ${n}.`];

const daySeed = (d: Date) => d.getFullYear() * 400 + d.getMonth() * 32 + d.getDate();

export function greetingFor(name: string, d = new Date()): string {
  const h = d.getHours();
  const dow = d.getDay(); // 0 Sun … 6 Sat
  const pool =
    h < 5 || h >= 23 ? LATE
    : dow === 0 || dow === 6 ? WEEKEND
    : dow === 1 && h < 12 ? MONDAY
    : dow === 5 && h >= 12 ? FRIDAY
    : h < 12 ? MORNING
    : h < 18 ? AFTERNOON
    : EVENING;
  return pool[daySeed(d) % pool.length]!(name);
}

export interface ValueQuote {
  valueId: 'drive' | 'reliability' | 'authenticity' | 'empathy';
  value: string;
  text: string;
  by: string;
}

export const VALUE_QUOTES: ValueQuote[] = [
  { valueId: 'drive', value: 'Drive', text: 'The best way out is always through.', by: 'Robert Frost' },
  { valueId: 'reliability', value: 'Reliability', text: 'Well done is better than well said.', by: 'Benjamin Franklin' },
  { valueId: 'authenticity', value: 'Authenticity', text: 'This above all: to thine own self be true.', by: 'William Shakespeare' },
  { valueId: 'empathy', value: 'Empathy', text: 'Seek first to understand, then to be understood.', by: 'Stephen R. Covey' },
  { valueId: 'drive', value: 'Drive', text: 'Well begun is half done.', by: 'Aristotle' },
  { valueId: 'reliability', value: 'Reliability', text: 'The details are not the details. They make the design.', by: 'Charles Eames' },
  { valueId: 'authenticity', value: 'Authenticity', text: 'I have no special talents. I am only passionately curious.', by: 'Albert Einstein' },
  { valueId: 'drive', value: 'Drive', text: 'Genius is one percent inspiration and ninety-nine percent perspiration.', by: 'Thomas Edison' },
  { valueId: 'reliability', value: 'Reliability', text: 'Design is not just what it looks like and feels like. Design is how it works.', by: 'Steve Jobs' },
  { valueId: 'empathy', value: 'Empathy', text: 'Alone we can do so little; together we can do so much.', by: 'Helen Keller' },
];

/** Rotates every three hours; the same quote for everyone at the same time. */
export const QUOTE_PERIOD_MS = 3 * 60 * 60 * 1000;
export function quoteFor(now = Date.now()): ValueQuote {
  return VALUE_QUOTES[Math.floor(now / QUOTE_PERIOD_MS) % VALUE_QUOTES.length]!;
}
